import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Trash2, Camera, X } from 'lucide-react';
import { useHealth } from '../../../contexts/HealthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useHealthPhoto } from '../../../hooks/useHealthPhoto';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Card from '../../../components/ui/Card';
import {
  HEALTH_RECORD_TYPES,
  ROUTES,
  VALIDATION,
} from '../../../config/constants';
import HealthRecordTypeChips from './HealthRecordTypeChips';

/**
 * Form de creación / edición de un record de salud.
 *
 * Props:
 *  - petId: string requerido
 *  - record?: si está, modo edit
 *
 * Campos:
 *  - type (chips)
 *  - title (siempre)
 *  - occurredAt (datetime-local — más útil que solo date porque a veces se
 *    registra justo después del evento)
 *  - weightKg (solo si type='weight')
 *  - productName (solo si type='vaccine' | 'deworming' | 'medication')
 *  - notes (siempre)
 */

const FIELD_ERROR_MAP = {
  INVALID_TITLE: 'title',
  INVALID_NOTES: 'notes',
  INVALID_TYPE: 'type',
  INVALID_OCCURRED_AT: 'occurredAt',
  INVALID_WEIGHT: 'weightKg',
  INVALID_PRODUCT_NAME: 'productName',
  CROSS_TENANT_PET: 'petId',
  PET_NOT_FOUND: 'petId',
};

const TYPES_WITH_WEIGHT = new Set([HEALTH_RECORD_TYPES.WEIGHT]);
const TYPES_WITH_PRODUCT = new Set([
  HEALTH_RECORD_TYPES.VACCINE,
  HEALTH_RECORD_TYPES.DEWORMING,
  HEALTH_RECORD_TYPES.MEDICATION,
]);

const dateToInputValue = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const timestampToDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toMillis === 'function') return new Date(ts.toMillis());
  if (ts instanceof Date) return ts;
  return null;
};

const HealthRecordForm = ({ petId, record = null }) => {
  const isEdit = Boolean(record);
  const {
    createHealthRecord,
    updateHealthRecord,
    softDeleteHealthRecord,
    uploadHealthRecordPhoto,
    deleteHealthRecordPhoto,
    getRecordById,
  } = useHealth();
  const toast = useToast();
  const navigate = useNavigate();

  // Default: ahora.
  const defaultOccurred = useMemo(
    () => (isEdit ? timestampToDate(record.occurredAt) ?? new Date() : new Date()),
    [isEdit, record]
  );

  const [type, setType] = useState(record?.type || HEALTH_RECORD_TYPES.CHECKUP);
  const [title, setTitle] = useState(record?.title || '');
  const [notes, setNotes] = useState(record?.notes || '');
  const [occurredAtInput, setOccurredAtInput] = useState(dateToInputValue(defaultOccurred));
  const [weightKg, setWeightKg] = useState(record?.weightKg != null ? String(record.weightKg) : '');
  const [productName, setProductName] = useState(record?.productName || '');

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [actionInFlight, setActionInFlight] = useState(null);

  // Foto: photoFile es lo que el usuario eligió ahora (todavía no subido).
  // photoCleared marca que el usuario tocó "Eliminar" sobre la foto actual.
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [photoCleared, setPhotoCleared] = useState(false);
  const fileInputRef = useRef(null);

  // Foto actual (solo si edit y todavía no se la limpió + no hay file nuevo)
  const { url: existingPhotoUrl } = useHealthPhoto(
    isEdit && !photoCleared && !photoFile ? record : null
  );

  const showsWeight = TYPES_WITH_WEIGHT.has(type);
  const showsProduct = TYPES_WITH_PRODUCT.has(type);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoCleared(false);
    // Preview local con ObjectURL.
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(URL.createObjectURL(file));
  };

  const handlePhotoClear = () => {
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(null);
    setPhotoFile(null);
    setPhotoCleared(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const previewUrl = photoPreviewUrl ?? (photoCleared ? null : existingPhotoUrl);

  const handleErrorFromService = (err) => {
    const field = FIELD_ERROR_MAP[err.code];
    if (field) {
      setErrors({ [field]: err.message });
    } else {
      toast.error(err.message || 'No se pudo guardar la observación.');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});

    if (!title.trim()) {
      setErrors({ title: 'El título es requerido' });
      return;
    }
    if (!occurredAtInput) {
      setErrors({ occurredAt: 'Elegí la fecha' });
      return;
    }
    const occurredDate = new Date(occurredAtInput);
    if (Number.isNaN(occurredDate.getTime())) {
      setErrors({ occurredAt: 'Fecha inválida' });
      return;
    }

    let weightValue = null;
    if (showsWeight) {
      if (weightKg.trim() !== '') {
        const parsed = Number(weightKg);
        if (Number.isNaN(parsed)) {
          setErrors({ weightKg: 'Peso inválido' });
          return;
        }
        weightValue = parsed;
      }
    }

    const productValue = showsProduct && productName.trim() ? productName.trim() : null;

    try {
      setSubmitting(true);

      let recordIdToUpdatePhoto = null;
      let recordRef = null;

      if (isEdit) {
        // Solo enviamos los campos que cambiaron, para mantener el log limpio.
        const updates = {};
        if (record.type !== type) updates.type = type;
        if (record.title !== title.trim()) updates.title = title;
        if ((record.notes || '') !== notes.trim()) {
          updates.notes = notes.trim() || null;
        }
        const recordOccurredMs = record.occurredAt?.toMillis?.() ?? 0;
        if (recordOccurredMs !== occurredDate.getTime()) {
          updates.occurredAt = occurredDate;
        }
        if ((record.weightKg ?? null) !== weightValue) {
          updates.weightKg = weightValue;
        }
        if ((record.productName ?? null) !== productValue) {
          updates.productName = productValue;
        }

        const hasFieldChanges = Object.keys(updates).length > 0;
        const hasPhotoChanges = photoFile != null || (photoCleared && record.photoExt);

        if (!hasFieldChanges && !hasPhotoChanges) {
          toast.info('Sin cambios.');
          navigate(ROUTES.PET_HEALTH.replace(':petId', petId));
          return;
        }

        if (hasFieldChanges) {
          await updateHealthRecord(record.id, updates);
        }
        recordIdToUpdatePhoto = record.id;
        recordRef = { id: record.id, petId };
      } else {
        const result = await createHealthRecord({
          petId,
          type,
          title,
          notes: notes.trim() || null,
          weightKg: weightValue,
          productName: productValue,
          occurredAt: occurredDate,
        });
        recordIdToUpdatePhoto = result.recordId;
        recordRef = { id: result.recordId, petId };
      }

      // Manejo de la foto. Si hay foto nueva, subimos; si se limpió la
      // existente (sin reemplazo), borramos. Errores de foto no abortan
      // el save (toast warning) — el record ya quedó persistido.
      if (photoFile && recordRef) {
        try {
          await uploadHealthRecordPhoto(recordRef, photoFile);
        } catch (photoErr) {
          toast.error(
            photoErr.message ||
              'La observación se guardó pero la foto no se pudo subir.'
          );
        }
      } else if (photoCleared && isEdit && record.photoExt) {
        try {
          await deleteHealthRecordPhoto(recordRef);
        } catch (photoErr) {
          toast.error(
            photoErr.message ||
              'La observación se guardó pero la foto no se pudo borrar.'
          );
        }
      }

      toast.success(isEdit ? 'Observación actualizada.' : 'Observación guardada.');
      navigate(ROUTES.PET_HEALTH.replace(':petId', petId));
    } catch (err) {
      handleErrorFromService(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    if (!window.confirm('¿Eliminar esta observación? Va a dejar registro en la bitácora pero desaparece del listado.')) return;
    try {
      setActionInFlight('delete');
      await softDeleteHealthRecord(record.id);
      toast.info('Observación eliminada.');
      navigate(ROUTES.PET_HEALTH.replace(':petId', petId));
    } catch (err) {
      toast.error(err.message || 'No se pudo eliminar.');
    } finally {
      setActionInFlight(null);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Card>
        <h2 className="font-semibold text-slate-900">Tipo</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          Elegí lo que mejor describa lo que querés anotar.
        </p>
        <div className="mt-3">
          <HealthRecordTypeChips
            value={type}
            onChange={setType}
            disabled={submitting}
          />
        </div>
        {errors.type && (
          <p role="alert" className="mt-2 text-sm text-status-red">{errors.type}</p>
        )}
      </Card>

      <Card>
        <Input
          label="Título"
          placeholder="Ej: Vacuna antirrábica anual"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={errors.title}
          maxLength={VALIDATION.HEALTH_RECORD_TITLE_MAX}
          autoFocus={!isEdit}
          disabled={submitting}
        />
        <div className="mt-3">
          <Input
            label="Fecha y hora del evento"
            type="datetime-local"
            value={occurredAtInput}
            onChange={(e) => setOccurredAtInput(e.target.value)}
            error={errors.occurredAt}
            disabled={submitting}
          />
        </div>
      </Card>

      {(showsWeight || showsProduct) && (
        <Card>
          {showsWeight && (
            <Input
              label="Peso (kg)"
              type="number"
              inputMode="decimal"
              step="0.01"
              placeholder="Ej: 5.4"
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
              error={errors.weightKg}
              min={VALIDATION.HEALTH_RECORD_WEIGHT_MIN_KG}
              max={VALIDATION.HEALTH_RECORD_WEIGHT_MAX_KG}
              disabled={submitting}
            />
          )}
          {showsProduct && (
            <div className={showsWeight ? 'mt-3' : ''}>
              <Input
                label="Producto / Marca"
                placeholder="Ej: Nobivac Rabies, Frontline Plus"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                error={errors.productName}
                maxLength={VALIDATION.HEALTH_RECORD_PRODUCT_NAME_MAX}
                disabled={submitting}
              />
            </div>
          )}
        </Card>
      )}

      <Card>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">
          Notas (opcional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={VALIDATION.HEALTH_RECORD_NOTES_MAX}
          disabled={submitting}
          placeholder="Detalles, indicaciones del vet, próximo control, etc."
          rows={4}
          className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:bg-slate-100 disabled:text-slate-500"
        />
        {errors.notes && (
          <p role="alert" className="mt-1.5 text-sm text-status-red">{errors.notes}</p>
        )}
      </Card>

      <Card>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">
          Foto (opcional)
        </label>
        <p className="mb-3 text-xs text-slate-500">
          Una imagen de la receta, la herida, la etiqueta del producto, etc.
        </p>
        {previewUrl ? (
          <div className="relative">
            <img
              src={previewUrl}
              alt="Foto de la observación"
              className="w-full max-h-72 rounded-xl object-cover"
            />
            <button
              type="button"
              onClick={handlePhotoClear}
              disabled={submitting}
              aria-label="Quitar foto"
              className="absolute top-2 right-2 rounded-full bg-white/95 p-1.5 shadow-md ring-1 ring-slate-200 hover:bg-white"
            >
              <X className="h-4 w-4 text-slate-700" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Camera className="h-5 w-5" aria-hidden="true" />
            Agregar foto
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          className="hidden"
          onChange={handlePhotoChange}
          disabled={submitting}
        />
      </Card>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          fullWidth
          onClick={() => navigate(ROUTES.PET_HEALTH.replace(':petId', petId))}
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          fullWidth
          isLoading={submitting}
          leftIcon={CheckCircle2}
        >
          {isEdit ? 'Guardar cambios' : 'Guardar'}
        </Button>
      </div>

      {isEdit && (
        <Card>
          <h2 className="font-semibold text-slate-900">Acciones</h2>
          <div className="mt-3">
            <Button
              type="button"
              variant="danger"
              fullWidth
              leftIcon={Trash2}
              onClick={handleDelete}
              isLoading={actionInFlight === 'delete'}
              disabled={Boolean(actionInFlight)}
            >
              Eliminar observación
            </Button>
          </div>
        </Card>
      )}
    </form>
  );
};

export default HealthRecordForm;
